import React, { useEffect, useState } from 'react';
import { Play, Square, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getActiveTimer,
  startTimer,
  stopTimer,
  discardTimer,
  type TimerSession,
  type TimeEntry,
} from '../packages/api-client/timetracking-api';
import { formatStopwatch } from '../utils/duration';

interface Props {
  taskId: string;
  /** Called with the recorded entry when a timer is stopped, so the log refreshes. */
  onStopped?: (entry: TimeEntry) => void;
}

/**
 * TaskTimer — live start/stop stopwatch for one task.
 *
 * One timer runs per user at a time. On mount it resumes any running timer via
 * GET /me/active-timer; if that timer is on another task it shows a hint and a
 * "Start here" action (which auto-stops the other timer server-side).
 */
export function TaskTimer({ taskId, onStopped }: Props) {
  const [active, setActive] = useState<TimerSession | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getActiveTimer().then(setActive).catch(() => {});
  }, []);

  const runningHere = active?.task_id === taskId;

  // Tick once a second while a timer for THIS task is running.
  useEffect(() => {
    if (!runningHere) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [runningHere, active?.id]);

  const elapsedSeconds = runningHere && active
    ? Math.max(0, Math.floor((now - new Date(active.started_at).getTime()) / 1000))
    : 0;

  async function handleStart() {
    setBusy(true);
    try {
      const sess = await startTimer(taskId);
      setActive(sess);
      setNow(Date.now());
    } catch {
      toast.error('Could not start timer');
    } finally {
      setBusy(false);
    }
  }

  async function handleStop() {
    setBusy(true);
    try {
      const entry = await stopTimer(taskId);
      setActive(null);
      onStopped?.(entry);
      toast.success('Timer stopped');
    } catch {
      toast.error('Could not stop timer');
    } finally {
      setBusy(false);
    }
  }

  async function handleDiscard() {
    setBusy(true);
    try {
      await discardTimer(taskId);
      setActive(null);
    } catch {
      toast.error('Could not discard timer');
    } finally {
      setBusy(false);
    }
  }

  // A timer is running on a different task.
  if (active && !runningHere) {
    return (
      <div className="task-timer task-timer-elsewhere">
        <span className="task-timer-hint">
          Timer running on <strong>{active.task_title || 'another task'}</strong>
        </span>
        <button className="btn btn-secondary btn-sm" onClick={handleStart} disabled={busy}>
          <Play size={14} aria-hidden="true" /> Start here
        </button>
      </div>
    );
  }

  if (runningHere) {
    return (
      <div className="task-timer task-timer-running">
        <span className="task-timer-display" role="timer" aria-label="Elapsed time">
          {formatStopwatch(elapsedSeconds)}
        </span>
        <button className="btn btn-primary btn-sm" onClick={handleStop} disabled={busy}>
          <Square size={14} aria-hidden="true" /> Stop
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={handleDiscard}
          disabled={busy}
          title="Discard timer"
          aria-label="Discard timer"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="task-timer">
      <button className="btn btn-primary btn-sm" onClick={handleStart} disabled={busy}>
        <Play size={14} aria-hidden="true" /> Start timer
      </button>
    </div>
  );
}
